import { useCallback, useEffect, useRef } from 'react';

import { useLazyQuery } from '@apollo/client/react';

import { get as getUser } from 'js/lib/user';

import AssignmentPassingThresholdQuery from 'bundles/assess-common/api/AssignmentPassingThreshold.graphql';
import type {
  AssignmentPassingThresholdQuery as AssignmentPassingThresholdQueryData,
  AssignmentPassingThresholdQueryVariables,
} from 'bundles/assess-common/api/__generated__/AssignmentPassingThreshold';
import { getWorkspaceV2Launcher } from 'bundles/labs-common/utils/WorkspaceApiUtils';
import GetCoachItemAssessmentConfigurationQuery from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/GetCoachItemAssessmentConfiguration.graphql';
import GetCourseBranchIdQuery from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/GetCourseBranchId.graphql';
import type {
  GetCoachItemAssessmentConfigurationQuery as GetCoachItemAssessmentConfigurationQueryData,
  GetCoachItemAssessmentConfigurationQueryVariables,
} from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetCoachItemAssessmentConfiguration';
import type {
  GetCourseBranchIdQuery as GetCourseBranchIdQueryData,
  GetCourseBranchIdQueryVariables,
} from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetCourseBranchId';
import { getSkillAssessmentMappingType } from 'bundles/skills-common/shared/verified-skills/utils/verifiedSkillsUtils';

const DEFAULT_ROLE_PLAY_PASSING_FRACTION = 1;

export type SkillAssessmentIdAndPassingFraction = {
  assessmentId: string;
  passingFraction: number | null;
  assessmentType: string;
};

type Props = {
  itemId: string;
  itemTypeName?: string;
  courseId: string;
};

const useGetSkillAssessmentIdAndPassingFraction = () => {
  const userId = getUser().id;

  const [fetchPassingThreshold] = useLazyQuery<
    AssignmentPassingThresholdQueryData,
    AssignmentPassingThresholdQueryVariables
  >(AssignmentPassingThresholdQuery, {
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'cache-first',
  });

  const [fetchCoachItemAssessmentConfiguration] = useLazyQuery<
    GetCoachItemAssessmentConfigurationQueryData,
    GetCoachItemAssessmentConfigurationQueryVariables
  >(GetCoachItemAssessmentConfigurationQuery, {
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'cache-first',
  });

  const [fetchCourseBranchId] = useLazyQuery<GetCourseBranchIdQueryData, GetCourseBranchIdQueryVariables>(
    GetCourseBranchIdQuery,
    {
      context: { clientName: 'gatewayGql' },
      fetchPolicy: 'cache-first',
    }
  );

  const fetchPassingThresholdRef = useRef(fetchPassingThreshold);
  const fetchCoachItemAssessmentConfigurationRef = useRef(fetchCoachItemAssessmentConfiguration);
  const fetchCourseBranchIdRef = useRef(fetchCourseBranchId);

  useEffect(() => {
    fetchPassingThresholdRef.current = fetchPassingThreshold;
    fetchCoachItemAssessmentConfigurationRef.current = fetchCoachItemAssessmentConfiguration;
    fetchCourseBranchIdRef.current = fetchCourseBranchId;
  });

  const fetchSkillAssessmentIdAndPassingFraction = useCallback(
    async ({ itemId, itemTypeName, courseId }: Props): Promise<SkillAssessmentIdAndPassingFraction> => {
      const assessmentType = getSkillAssessmentMappingType(itemTypeName);

      if (assessmentType === 'lab') {
        const launcher = await getWorkspaceV2Launcher(courseId, itemId);

        return {
          assessmentId: launcher?.versionedAtomId || '',
          passingFraction: null,
          assessmentType,
        };
      }

      if (assessmentType === 'roleplay') {
        const branchResult = await fetchCourseBranchIdRef.current({ variables: { courseId } });
        const branchId = branchResult.data?.Course?.queryById?.myBranchProgress?.courseBranchId ?? courseId;

        const configurationResult = await fetchCoachItemAssessmentConfigurationRef.current({
          variables: {
            userId: `${userId}`,
            courseId,
            branchId,
            itemId,
          },
        });

        if (configurationResult.error) {
          throw configurationResult.error;
        }

        const configuration = configurationResult.data?.CoachItem?.getCoachItemConfiguration;
        const { assessmentId: unversionedAssessmentId, assessmentVersion, passingFraction } = configuration ?? {};

        return {
          assessmentId:
            unversionedAssessmentId && assessmentVersion != null
              ? `${unversionedAssessmentId}@${assessmentVersion}`
              : '',
          passingFraction: passingFraction ?? DEFAULT_ROLE_PLAY_PASSING_FRACTION,
          assessmentType,
        };
      }

      const passingThresholdResult = await fetchPassingThresholdRef.current({
        variables: {
          itemId,
          courseId,
        },
      });

      if (
        passingThresholdResult.error ||
        passingThresholdResult.data?.SubmissionState?.queryState?.__typename !== 'Submission_SubmissionState'
      ) {
        throw new Error('Failed to fetch passing threshold');
      }

      const queryState = passingThresholdResult.data?.SubmissionState?.queryState;
      const assignmentState = queryState?.__typename === 'Submission_SubmissionState' ? queryState.assignment : null;

      return {
        assessmentId: assignmentState?.id?.split('@')[0] || '',
        passingFraction: assignmentState?.passingFraction ?? 0,
        assessmentType,
      };
    },
    [userId]
  );

  return { fetchSkillAssessmentIdAndPassingFraction };
};

export default useGetSkillAssessmentIdAndPassingFraction;
